import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {environment} from '../../environments/environment';
import {forkJoin, Observable, of} from 'rxjs';
import {map, mergeMap} from 'rxjs/operators';
import {Product} from '../models/products/product.model';
import {ProductUpdate} from '../models/products/product.update';
import {ProductImage} from '../models/products/productImage.model';
import {JobProduct} from '../models/products/jobProduct.model';

@Injectable({
  providedIn: 'root'
})
export class ProductsService {

  constructor(private http: HttpClient) {
  }

  private PRODUCTS_URL: string = environment.BASE_URL + 'products';
  private JOB_URL: string = environment.BASE_URL + 'job';

  public getProducts(): Observable<Array<Product>> {
    return this.http.get<{ products: Array<Product> }>(this.PRODUCTS_URL).pipe(
      map(response => response.products),
      map(products => products.map(this.mapProduct))
    );
  }

  public getProduct(id: string): Observable<Product> {
    return this.http.get<{ product: Product }>(`${this.PRODUCTS_URL}/${id}`).pipe(
      map(response => response.product),
      map(this.mapProduct)
    );
  }

  public createProduct(product: Product, images: ProductImage[]): Observable<any> {
    return this.http.post<{ product: Product }>(`${this.PRODUCTS_URL}`, product).pipe(
      map(response => {
        product.id = response.product.id;
        return response.product;
      }),
      product.images.length > 0 ?
        mergeMap((response: Product) => forkJoin(this.updateProductImages(response, images))) :
        map(response => null),
      mergeMap(() => this.getProduct(product.id)),
    );
  }

  public updateProduct(product: ProductUpdate, images: ProductImage[]): Observable<Product> {
    return this.http.patch<{ product: Product }>(`${this.PRODUCTS_URL}/${product.id}`, product).pipe(
      map(response => {
        product.id = response.product.id;
        return response.product;
      }),
      product.images.length > 0 ?
        mergeMap((response: Product) => forkJoin(this.updateProductImages(response, images))) :
        map(response => null),
      mergeMap(() => this.getProduct(product.id)),
    );
  }

  public deleteProduct(product: Product): Observable<Product> {
    return this.http.delete<{ product: Product }>(`${this.PRODUCTS_URL}/${product.id}`).pipe(
      map(response => {
        return response.product;
      }),
    );
  }

  public getJobProducts(jobId: string): Observable<Array<JobProduct>> {
    return this.http.get<{ products: Array<JobProduct> }>(`${this.JOB_URL}/${jobId}/product`).pipe(
      map(response => {
        return response.products;
      }),
    );
  }

  public updateJobProducts(jobId: string, products: Array<JobProduct>): Observable<Array<JobProduct>> {
    return this.http.patch<{ products: Array<JobProduct> }>(`${this.JOB_URL}/${jobId}/product`, {products}).pipe(
      map(response => {
        return response.products;
      }),
    );
  }

  private updateProductImages(product: Product, images: ProductImage[]): Observable<any>[] {
    return product.images
      .filter((productImage: ProductImage) => productImage.action === 'CREATE' || productImage.action === 'UPDATE')
      .map((productImage: ProductImage) => {
        const imageIndex = images.findIndex((img) => productImage.id === img.id);
        let image;
        if (imageIndex >= 0) {
          image = images.splice(imageIndex, 1)[0];
        } else {
          image = images.pop();
        }
        return this.updateImage(productImage.uploadUrl, image.file);
      });
  }

  private updateImage(url: string, image: File): Observable<any> {
    return this.http.put(url, image, {
      headers: {
        'x-amz-acl': 'public-read',
        'content-type': image.type,
      },
      reportProgress: true
    });
  }

  private mapProduct(product: Product): Product {
    product.images = product.images.map((image: any) => ({
      url: image.url,
      id: image.id,
      mimeType: image.mimeType,
      file: null,
      action: 'NOTHING',
      uploadUrl: ''
    }));
    return product;
  }

}
